import { Fragment } from 'react';

import { Button } from '#/components/Button/Button';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { Stack } from '#/components/Stack/Stack';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { BodySmall } from '#/components/Text/BodySmall';
import { H4 } from '#/components/Text/H4';
import { useShowMore } from '#/hooks/useShowMore';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiUserProfileWorkHistoryJob } from '#/modules/userProfile/api/types/ApiUserProfileWorkHistoryJob';
import { useFormatUserProfileWorkHistoryJobItem } from '#/modules/userProfile/hooks/useUserProfileFormatWorkHistoryJobItem';
import { spacing } from '#/theme/theme';

type Props = {
  workHistoryJob: ApiUserProfileWorkHistoryJob;
};

export function ViewUserProfileWorkHistoryJobItem({ workHistoryJob }: Props) {
  const { locale } = useI18n();
  const { dateRange, employmentType } =
    useFormatUserProfileWorkHistoryJobItem(workHistoryJob);

  const { canShowMore, visibleItems, showMore, toggleShowMore } = useShowMore(
    [workHistoryJob.description ?? ''],
    200,
  );

  return (
    <Stack
      qaId="work-history-job"
      direction="column"
      key={workHistoryJob.id}
      spacing={spacing[16]}
    >
      <H4 lineHeight={1} margin={0}>
        {workHistoryJob.titleSuggestion ?? workHistoryJob.text}
      </H4>
      <BodySmall lineHeight={1} margin={0}>
        {workHistoryJob.org?.name && (
          <>
            {workHistoryJob.org.url ? (
              <StyledNativeLinkWithRef
                qaId="work-history-job-org-link"
                href={workHistoryJob.org.url[locale]}
              >
                {workHistoryJob.org.name}
              </StyledNativeLinkWithRef>
            ) : (
              workHistoryJob.org.name
            )}
            {', '}
          </>
        )}
        {employmentType}
      </BodySmall>
      <BodyMicro lineHeight={1} margin={0}>
        {dateRange}
      </BodyMicro>
      {workHistoryJob.description ? (
        <BodySmall>
          {Array.isArray(visibleItems)
            ? visibleItems.map((item, index) => (
                // oxlint-disable-next-line react/no-array-index-key
                <Fragment key={index}>{String(item)}</Fragment>
              ))
            : visibleItems}
          {canShowMore && !showMore ? (
            <Button
              noPadding
              variant="tertiary-grey"
              type="button"
              onClick={() => toggleShowMore()}
            >
              ...see more
            </Button>
          ) : null}
        </BodySmall>
      ) : null}
    </Stack>
  );
}
